import React, { useState, useEffect, useRef } from 'react';
import s_img from '../scratch_foreground.jpg';

export default function Chance({ page, nextPage, wording, img, b, setCurrentPage, selectedCards, setHasWon }) {
    const [showCard, setShowCard] = useState(false);
    const [suspensePhase, setSuspensePhase] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [loadedImages, setLoadedImages] = useState({ scratch: false, prize: false });

    // Color palette based on brand
    const colors = b === 2 ? {
        bgFrom: '#8B2500',
        bgVia: '#C4621F',
        bgTo: '#E87722',
        primary: '#F59E42',
        primaryLight: '#FFA94D',
        textAccent: '#FED7AA',
        textLight: '#FFEDD5',
        glow: 'rgba(245, 158, 66, 0.3)',
        glowBorder: 'rgba(245, 158, 66, 0.5)'
    } : {
        bgFrom: '#1A0D2E',
        bgVia: '#5D1A5E',
        bgTo: '#B81F7B',
        primary: '#EC4899',
        primaryLight: '#F472B6',
        textAccent: '#F9A8D4',
        textLight: '#FBCFE8',
        glow: 'rgba(236, 72, 153, 0.3)',
        glowBorder: 'rgba(236, 72, 153, 0.5)'
    };

    // Preload images
    useEffect(() => {
        const loadImage = (src, key) => {
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => {
                    setLoadedImages(prev => ({ ...prev, [key]: true }));
                    resolve();
                };
                image.onerror = reject;
                image.src = src;
            });
        };

        Promise.all([
            loadImage(s_img, 'scratch'),
            loadImage(img, 'prize')
        ]).then(() => {
            setImagesLoaded(true);
            setTimeout(() => setSuspensePhase(true), 300);
            setTimeout(() => setShowCard(true), 2500);
        }).catch(error => {
            console.error('Error loading images:', error);
            setImagesLoaded(true);
            setTimeout(() => setSuspensePhase(true), 300);
            setTimeout(() => setShowCard(true), 2500);
        });
    }, [img]);

    useEffect(() => {
        if (showCard) {
            const timer = setTimeout(() => {
                if(selectedCards.length > 2 || setHasWon){
                    setCurrentPage(4);     
                    return;
                }
                nextPage();
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [showCard, selectedCards, setHasWon, setCurrentPage, nextPage]);

    return (
        <div 
            className='flex flex-col items-center justify-center w-full min-h-screen relative overflow-hidden px-6' 
            style={{ 
                background: `linear-gradient(to bottom right, ${colors.bgFrom}, ${colors.bgVia}, ${colors.bgTo})`,
            }}
        >
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-10 right-10 w-32 h-32 rounded-full blur-3xl animate-float" style={{ backgroundColor: colors.glow }}></div>
                <div className="absolute bottom-20 left-10 w-40 h-40 rounded-full blur-3xl animate-float-delayed" style={{ backgroundColor: colors.glow }}></div>
                <div className="absolute top-1/3 left-1/4 w-36 h-36 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: colors.glow }}></div>
            </div>

            {!imagesLoaded ? (
                <div className="relative z-10 flex flex-col items-center justify-center">
                    <div className="relative">
                        <div className="w-20 h-20 border-4 rounded-full animate-spin" 
                            style={{ 
                                borderColor: colors.primary,
                                borderTopColor: 'transparent'
                            }}>
                        </div>
                        <div className="absolute inset-0 w-20 h-20 border-4 rounded-full animate-ping" 
                            style={{ 
                                borderColor: colors.primaryLight,
                                opacity: 0.3
                            }}>
                        </div>
                    </div>
                    <p className="mt-6 text-white text-lg font-medium animate-pulse">
                        Loading your surprise...
                    </p>
                </div>
            ) : (
                <div className="relative z-10 flex flex-col items-center w-full max-w-md">
                    {suspensePhase && !showCard && (
                        <div className="relative w-72 h-72 flex items-center justify-center">
                            <div className="absolute inset-0 animate-spin-slow">
                                <div className="absolute top-0 left-1/2 w-4 h-4 rounded-full -translate-x-1/2" style={{ backgroundColor: colors.primary, boxShadow: `0 0 20px ${colors.primary}` }}></div>
                                <div className="absolute bottom-0 left-1/2 w-4 h-4 rounded-full -translate-x-1/2" style={{ backgroundColor: colors.primaryLight, boxShadow: `0 0 20px ${colors.primaryLight}` }}></div>
                                <div className="absolute left-0 top-1/2 w-4 h-4 rounded-full -translate-y-1/2" style={{ backgroundColor: colors.primary, boxShadow: `0 0 20px ${colors.primary}` }}></div>
                                <div className="absolute right-0 top-1/2 w-4 h-4 rounded-full -translate-y-1/2" style={{ backgroundColor: colors.primaryLight, boxShadow: `0 0 20px ${colors.primaryLight}` }}></div>
                            </div>
                            <div className="absolute inset-0 animate-spin-reverse">
                                <div className="absolute top-1/4 left-1/4 w-3 h-3 rounded-full" style={{ backgroundColor: colors.primaryLight, boxShadow: `0 0 15px ${colors.primaryLight}` }}></div>
                                <div className="absolute top-1/4 right-1/4 w-3 h-3 rounded-full" style={{ backgroundColor: colors.primary, boxShadow: `0 0 15px ${colors.primary}` }}></div>
                                <div className="absolute bottom-1/4 left-1/4 w-3 h-3 rounded-full" style={{ backgroundColor: colors.primary, boxShadow: `0 0 15px ${colors.primary}` }}></div>
                                <div className="absolute bottom-1/4 right-1/4 w-3 h-3 rounded-full" style={{ backgroundColor: colors.primaryLight, boxShadow: `0 0 15px ${colors.primaryLight}` }}></div>
                            </div>
                            <div className="absolute inset-0 rounded-full animate-pulse-intense" style={{ border: `2px solid ${colors.primary}`, boxShadow: `0 0 40px ${colors.glow}` }}></div>
                            <div className="absolute inset-8 rounded-full animate-pulse-intense-delayed" style={{ border: `2px solid ${colors.primaryLight}`, boxShadow: `0 0 30px ${colors.glow}` }}></div>
                        </div>
                    )}

                    {showCard && (
                        <div className={`relative transition-all duration-[1500ms] ease-out ${showCard ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-0 rotate-180'}`}>
                            <div className="absolute -inset-4 rounded-3xl blur-2xl animate-pulse-glow" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})`, opacity: 0.3 }}></div>
                            <div className="relative bg-white/10 backdrop-blur-sm p-4 rounded-3xl shadow-2xl border-4 border-white/30">
                                <div className="absolute top-2 left-2 w-4 h-4 border-t-4 border-l-4 rounded-tl-lg" style={{ borderColor: colors.primary }}></div>
                                <div className="absolute top-2 right-2 w-4 h-4 border-t-4 border-r-4 rounded-tr-lg" style={{ borderColor: colors.primary }}></div>
                                <div className="absolute bottom-2 left-2 w-4 h-4 border-b-4 border-l-4 rounded-bl-lg" style={{ borderColor: colors.primary }}></div>
                                <div className="absolute bottom-2 right-2 w-4 h-4 border-b-4 border-r-4 rounded-br-lg" style={{ borderColor: colors.primary }}></div>
                                <div className='flex justify-center items-center bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl shadow-inner'>
                                    <img height={300} width={300} src={img} alt="Prize" className="rounded-2xl"/>
                                </div>
                            </div>
                            <div className="absolute -top-2 -left-2 w-3 h-3 rounded-full animate-ping" style={{ backgroundColor: colors.primary }}></div>
                            <div className="absolute -top-2 -right-2 w-3 h-3 rounded-full animate-ping" style={{ backgroundColor: colors.primaryLight, animationDelay: '0.5s' }}></div>
                            <div className="absolute -bottom-2 -left-2 w-3 h-3 rounded-full animate-ping" style={{ backgroundColor: colors.primary, animationDelay: '1s' }}></div>
                            <div className="absolute -bottom-2 -right-2 w-3 h-3 rounded-full animate-ping" style={{ backgroundColor: colors.primaryLight, animationDelay: '1.5s' }}></div>
                        </div>
                    )}
                </div>
            )}

            <style jsx>{`
                @keyframes float { 0%, 100% { transform: translateY(0px) translateX(0px); } 50% { transform: translateY(-20px) translateX(10px); } }
                @keyframes float-delayed { 0%, 100% { transform: translateY(0px) translateX(0px); } 50% { transform: translateY(20px) translateX(-10px); } }
                @keyframes pulse-glow { 0%, 100% { opacity: 0.5; } 50% { opacity: 0.8; } }
                @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes spin-reverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
                @keyframes pulse-intense { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.1); opacity: 0.7; } }
                @keyframes pulse-intense-delayed { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: 0.6; } }
                .animate-float { animation: float 6s ease-in-out infinite; }
                .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
                .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
                .animate-spin-slow { animation: spin-slow 2s linear infinite; }
                .animate-spin-reverse { animation: spin-reverse 3s linear infinite; }
                .animate-pulse-intense { animation: pulse-intense 1s ease-in-out infinite; }
                .animate-pulse-intense-delayed { animation: pulse-intense-delayed 1s ease-in-out infinite 0.5s; }
            `}</style>
        </div>
    );
}